import React, { useEffect, useMemo, useState } from 'react';
import { sendDialogData } from '../../api/fetchDialogs';
import { updatePathPracticeProgress } from '../../api/fetchPath';
import DialogScreen from '../Dialogs/DialogScreen';
import styles from './PathTopicHub.module.css';

const formatName = (code) =>
  String(code || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const PathDialogs = ({ userId, step, onBack }) => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDialogId, setActiveDialogId] = useState(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        setLoading(true);
        const data = await sendDialogData({ userId });
        if (active) setTopics(Array.isArray(data) ? data : []);
      } catch (_) {
        if (active) setTopics([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [userId]);

  const allowedTopicCodes = useMemo(() => {
    const topicCode = step?.repeat_pool?.topicCode || step?.focus_topic || null;
    return topicCode ? [topicCode] : (step?.topic_codes || []);
  }, [step?.focus_topic, step?.repeat_pool?.topicCode, step?.topic_codes]);

  const filteredTopics = useMemo(() => {
    const allowed = new Set(allowedTopicCodes);
    return topics.filter((topic) => allowed.has(topic.code)).slice(0, 3);
  }, [allowedTopicCodes, topics]);

  useEffect(() => {
    if (!step?.focus_topic || filteredTopics.length === 0 || activeDialogId) return;
    const hit = filteredTopics.find((topic) => topic.code === step.focus_topic);
    if (hit) setActiveDialogId(hit.id);
  }, [activeDialogId, filteredTopics, step?.focus_topic]);

  useEffect(() => {
    if (allowedTopicCodes.length !== 1 || filteredTopics.length !== 1 || activeDialogId) return;
    setActiveDialogId(filteredTopics[0].id);
  }, [activeDialogId, allowedTopicCodes.length, filteredTopics]);

  if (activeDialogId) {
    return (
      <DialogScreen
        dialogId={activeDialogId}
        userId={userId}
        onBack={async () => {
          if (step?.id) {
            try {
              await updatePathPracticeProgress({ userId, stepId: step.id, delta: 1 });
            } catch (_) {}
          }
          setActiveDialogId(null);
          onBack?.();
        }}
      />
    );
  }

  if (loading) {
    return <div className={styles.loading}>Загрузка...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <button className={styles.backButton} onClick={onBack}>Назад</button>
      </div>

      <div className={styles.title}>Диалоги</div>
      <div className={styles.subtitle}>Выберите один из трёх диалогов для этого checkpoint-блока.</div>

      <div className={styles.row}>
        {filteredTopics.map((topic) => (
          <button
            className={styles.topicButton}
            key={topic.id}
            onClick={() => setActiveDialogId(topic.id)}
          >
            <div className={styles.circle}>
              <div className={styles.circleLabel}>{formatName(topic.code)}</div>
            </div>
            <div className={styles.topicName}>{topic.title || formatName(topic.code)}</div>
          </button>
        ))}
      </div>

      {filteredTopics.length === 0 && <div className={styles.empty}>Подходящие диалоги не найдены</div>}
    </div>
  );
};

export default PathDialogs;
